import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useSelector } from "react-redux";
import { classroomInsUpdSlice, selectClassroomInsUpd } from "../../../redux/classroom/classroomInsUpdSlice";
import { useAppDispatch } from "../../../app/hooks";
import { retrieveSchoolRoutine, schoolRoutinesSlice, selectSchoolRoutines } from "../../../redux/school/schoolRoutinesSlice";
import { useEffect, useState } from "react";
import { EnabledEnum, getEnabledEnumName, ImageProcessorStyle, LoadState } from "../../../redux/common";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { selectUser } from "../../../redux/user/userSlice";
import { ClassroomInfo, ClassroomStatus } from "../../../redux/classroom/classroomSlice";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AvatarUploadDragAndDrop from "../../../features/base/image/AvatarUploadDragAndDrop";

export function ClassroomInsUpdData() {
    const classroomInsUpd = useSelector(selectClassroomInsUpd);
    const dispatch = useAppDispatch();
    const routines = useSelector(selectSchoolRoutines);
    const user = useSelector(selectUser);

    const classroom = classroomInsUpd.classroom;

    const [helpOpenStatus, setHelpOpenStatus] = useState(false);
    const [helpOpenReview, setHelpOpenReview] = useState(false);
    const [helpOpenHomework, setHelpOpenHomework] = useState(false);
    const [helpOpenCurriculum, setHelpOpenCurriculum] = useState(false);

    useEffect(() => {
        dispatch(schoolRoutinesSlice.actions.check(classroom.schoolId));
        if (routines.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchoolRoutine({ schoolId: classroom.schoolId }));
        }
    }, [classroom, routines.schoolId, user]);


    const changedName = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const classroom = {
            ...classroomInsUpd.classroom
        };
        classroom.name = event.target.value;
        dispatch(classroomInsUpdSlice.actions.set(classroom));
    };

    const changedYear = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const classroom = {
            ...classroomInsUpd.classroom
        };
        classroom.year = event.target.value;
        dispatch(classroomInsUpdSlice.actions.set(classroom));
    };

    const changedRoutine = (event: SelectChangeEvent) => {
        event.preventDefault();
        const classroom = {
            ...classroomInsUpd.classroom
        };
        classroom.routineId = event.target.value;
        dispatch(classroomInsUpdSlice.actions.set(classroom));
    };

    const handleAvatarChange = (img: string) => {
        const classroom = {
            ...classroomInsUpd.classroom,
            url: img
        };
        dispatch(classroomInsUpdSlice.actions.set(classroom));
    };


    let selectEl = undefined;
    if(routines.state.state === LoadState.Failed) {
        selectEl = <Stack>
            <Typography color="error">Erro ao carregar rotinas</Typography>
            <Button variant="outlined" onClick={() => {
                dispatch(retrieveSchoolRoutine({ schoolId: classroom.schoolId }));
            }}>Tentar novamente</Button>
        </Stack>;
    } else if(routines.state.state === LoadState.Loading
                || routines.state.state === LoadState.NotStarted) {
        selectEl = <Typography>
            Carregando rotinas...
        </Typography>;
    } else if(routines.list?.length === 0) {
        selectEl = <Typography sx={{ border: 1, p: 1, bgcolor: "background.paper", borderRadius: 2 }}>
            Nenhuma rotina cadastrada
        </Typography>;
    } else {
        selectEl = <FormControl fullWidth>
            <InputLabel id="routine-label">Rotina</InputLabel>
            <Select
                labelId="routine-label"
                id="routine-select"
                value={`${classroom.routineId}`}
                label="Rotina"
                onChange={changedRoutine}
            >
                <MenuItem value={undefined}>Sem rotina</MenuItem>
                {routines.list?.map((routine: ClassroomInfo) => (
                    <MenuItem key={routine.id} value={routine.id}>{routine.name}</MenuItem>
                ))}
            </Select>
        </FormControl>;
    }

    return <>
        <Typography variant="h6" gutterBottom>
            Dados da sala de aula
        </Typography>
        <Grid container spacing={3}>
            <Grid size={12}>
                <TextField required id="name" name="name" label="Nome"
                    fullWidth autoComplete="nome" variant="standard"
                    value={classroomInsUpd.classroom.name}
                    onChange={changedName} />
                <TextField id="year" name="year" label="Ano"
                    fullWidth autoComplete="ano" variant="standard"
                    value={classroomInsUpd.classroom.year}
                    onChange={changedYear} />
            </Grid>
            <Grid size={12}>
                {selectEl}
            </Grid>
            <Grid size={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl fullWidth>
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            id="status-select"
                            value={classroomInsUpd.classroom.classroomStatus ?? 0}
                            label="Status"
                            onChange={e => {
                                const classroom = {
                                    ...classroomInsUpd.classroom,
                                    classroomStatus: Number(e.target.value)
                                };
                                dispatch(classroomInsUpdSlice.actions.set(classroom));
                            }}
                        >
                            <MenuItem value={ClassroomStatus.ENABLED}>Ativa</MenuItem>
                            <MenuItem value={ClassroomStatus.DONE}>Finalizada</MenuItem>
                            <MenuItem value={ClassroomStatus.NOT_READY}>Cadastro em andamento</MenuItem>
                        </Select>
                    </FormControl>
                    <Button sx={{ ml: 1, minWidth: 0 }} onClick={() => setHelpOpenStatus(true)}><HelpOutlineIcon /></Button>
                </Box>
                <Dialog open={helpOpenStatus} onClose={() => setHelpOpenStatus(false)}>
                    <DialogTitle>Status da Sala de Aula</DialogTitle>
                    <DialogContent>
                        <Typography variant="h6">
                            Ativa
                        </Typography>
                        <Typography variant="body1">
                            Sala de aula em funcionamento normal.<br/>
                            Os pais podem ver a sala de aula, as conversas estão criadas e ativas para uso.
                        </Typography>
                        <br/>
                        <Typography variant="h6">
                            Finalizada
                        </Typography>
                        <Typography variant="body1">
                            Sala de aula encerrada.<br/>
                            Os pais podem ver a sala de aula, mas as conversas estão bloqueadas.
                        </Typography>
                        <br/>
                        <Typography variant="h6">
                            Cadastro em andamento
                        </Typography>
                        <Typography variant="body1">
                            Sala de aula ainda não está pronta para uso.<br/>
                            Os pais não podem ver a sala de aula, e as conversas não foram criadas.<br/>
                            <i>Caso a sala tenha sido colocada em funcionamento e alterada para este status, as conversas já criadas serão bloqueadas.</i>
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpenStatus(false)}>Fechar</Button>
                    </DialogActions>
                </Dialog>
            </Grid>
             <Grid size={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl fullWidth>
                        <InputLabel id="image-processor-style-label">Revisão de Fotos</InputLabel>
                        <Select
                            labelId="image-processor-style-label"
                            id="image-processor-style-select"
                            value={classroomInsUpd.classroom.imageProcessorStyle ?? ImageProcessorStyle.DIRECT}
                            label="Revisão de Fotos"
                            onChange={e => {
                                const classroom = {
                                    ...classroomInsUpd.classroom,
                                    imageProcessorStyle: Number(e.target.value)
                                } as ClassroomInfo;
                                dispatch(classroomInsUpdSlice.actions.set(classroom));
                            }}
                        >
                            <MenuItem value={ImageProcessorStyle.DIRECT}>Sem revisão</MenuItem>
                            <MenuItem value={ImageProcessorStyle.REVIEW_REQUIRED}>Revisão obrigatória</MenuItem>
                        </Select>
                    </FormControl>
                    <Button sx={{ ml: 1, minWidth: 0 }} onClick={() => setHelpOpenReview(true)}><HelpOutlineIcon /></Button>
                </Box>
                <Dialog open={helpOpenReview} onClose={() => setHelpOpenReview(false)}>
                    <DialogTitle>Revisão de imagem</DialogTitle>
                    <DialogContent>
                        <Typography variant="h6">
                            Sem revisão
                        </Typography>
                        <Typography variant="body1">
                            As imagens enviadas para a sala de aula serão enviadas para os pais assim que possível.
                        </Typography>
                        <br/>
                        <Typography variant="h6">
                            Revisão Obrigatória
                        </Typography>
                        <Typography variant="body1">
                            As imagens enviadas para a sala de aula precisarão ser revisadas por alguém autorizado antes de serem enviadas para os pais.<br/>
                            Se concedida permissão, o próprio usuário que enviou a foto poderá realizar a revisão.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpenReview(false)}>Fechar</Button>
                    </DialogActions>
                </Dialog>
            </Grid>



            <Grid size={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl fullWidth>
                        <InputLabel id="homework-style-label">Lições de casa</InputLabel>
                        <Select
                            labelId="homework-style-label"
                            id="homework-style-select"
                            value={classroomInsUpd.classroom.homework ?? EnabledEnum.NO}
                            label="Lições de casa"
                            onChange={e => {
                                const classroom = {
                                    ...classroomInsUpd.classroom,
                                    homework: Number(e.target.value)
                                } as ClassroomInfo;
                                dispatch(classroomInsUpdSlice.actions.set(classroom));
                            }}
                        >
                            <MenuItem value={EnabledEnum.NO}>{getEnabledEnumName(EnabledEnum.NO)}</MenuItem>
                            <MenuItem value={EnabledEnum.YES}>{getEnabledEnumName(EnabledEnum.YES)}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button sx={{ ml: 1, minWidth: 0 }} onClick={() => setHelpOpenHomework(true)}>
                        <HelpOutlineIcon />
                    </Button>
                </Box>
                <Dialog open={helpOpenHomework} onClose={() => setHelpOpenHomework(false)}>
                    <DialogTitle>Lições de casa</DialogTitle>
                    <DialogContent>
                        <Typography variant="h6">
                            Ativa
                        </Typography>
                        <Typography variant="body1">
                            A sala possue lições de casa. Será possível criar lições de casa para os alunos, e os pais poderão visualizá-las e os professores poderão marcar as lições de casa como realizadas ou não pelos alunos.
                        </Typography>
                        <br/>
                        <Typography variant="h6">
                            Inativa
                        </Typography>
                        <Typography variant="body1">
                            A sala não possue lições de casa. Não será possível criar lições de casa para os alunos.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpenHomework(false)}>Fechar</Button>
                    </DialogActions>
                </Dialog>
            </Grid>



            <Grid size={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl fullWidth>
                        <InputLabel id="curriculum-style-label">Grade Curricular</InputLabel>
                        <Select
                            labelId="curriculum-style-label"
                            id="curriculum-style-select"
                            value={classroomInsUpd.classroom.curriculum ?? EnabledEnum.NO}
                            label="Grade Curricular"
                            onChange={e => {
                                const classroom = {
                                    ...classroomInsUpd.classroom,
                                    curriculum: Number(e.target.value)
                                } as ClassroomInfo;
                                dispatch(classroomInsUpdSlice.actions.set(classroom));
                            }}
                        >
                            <MenuItem value={EnabledEnum.NO}>{getEnabledEnumName(EnabledEnum.NO)}</MenuItem>
                            <MenuItem value={EnabledEnum.YES}>{getEnabledEnumName(EnabledEnum.YES)}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button sx={{ ml: 1, minWidth: 0 }} onClick={() => setHelpOpenCurriculum(true)}>
                        <HelpOutlineIcon />
                    </Button>
                </Box>
                <Dialog open={helpOpenCurriculum} onClose={() => setHelpOpenCurriculum(false)}>
                    <DialogTitle>Grade Curricular</DialogTitle>
                    <DialogContent>
                        <Typography variant="h6">
                            Ativa
                        </Typography>
                        <Typography variant="body1">
                            A sala possue grade curricular. Desta forma, será possível criar uma grade curricular para a sala de aula, associar as horários com cada disciplina, e os pais poderão visualizar os horários das aulas por dia da semana.
                        </Typography>
                        <br/>
                        <Typography variant="h6">
                            Inativa
                        </Typography>
                        <Typography variant="body1">
                            A sala não possue grade curricular. Os pais não terão a visão dos horários das aulas por dia da semana.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpenHomework(false)}>Fechar</Button>
                    </DialogActions>
                </Dialog>
            </Grid>


            <Grid size={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Avatar
                </Typography>
                <AvatarUploadDragAndDrop
                    value={classroomInsUpd.classroom.url || ""}
                    onChange={handleAvatarChange}
                    alt={classroomInsUpd.classroom.name}
                />
            </Grid>
        </Grid>
        </>;
}